// /apps/patient/src/pages/billing/PaymentSuccess.tsx

import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { patientApiService } from '../../services/api';
import { Invoice, Payment } from '@prism-ehr/shared';
import { CheckCircle, Download, FileText, Home, Receipt, Calendar } from 'lucide-react';
import { formatDate } from '@prism-ehr/shared';

export function PaymentSuccess() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [payment, setPayment] = useState<Payment | null>(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const locationState = location.state as any;

  const paymentType = searchParams.get('type') || locationState?.type;
  const isNonInvoice = paymentType === 'subscription' || paymentType === 'package' || id === 'complete';

  useEffect(() => {
    if (isNonInvoice) {
      setLoading(false);
      return;
    }

    const paymentIntentId = searchParams.get('payment_intent');

    if (paymentIntentId && id) {
      confirmPayment(paymentIntentId);
    } else if (id) {
      loadInvoiceDetails();
    }
  }, [id, searchParams]);

  const getSuccessMessage = () => {
    if (paymentType === 'package') {
      return 'Your package has been purchased successfully!';
    } else if (paymentType === 'subscription') {
      return 'Your subscription has been activated!';
    }
    return 'Your payment has been processed successfully.';
  };

  const confirmPayment = async (paymentIntentId: string) => {
    try {
      const response = await patientApiService.confirmPayment(paymentIntentId, id!);
      setPayment(response.payment);
      setInvoice(response.invoice);
    } catch (error) {
      console.error('Error confirming payment:', error);
      // Payment may have already been confirmed
      loadInvoiceDetails();
    } finally {
      setLoading(false);
    }
  };

  const loadInvoiceDetails = async () => {
    try {
      const invoiceData = await patientApiService.getInvoiceDetail(id!);
      setInvoice(invoiceData);

      // Load the most recent payment
      const paymentsResponse = await patientApiService.getMyPayments({
        invoice_id: id,
        perPage: 1
      });

      if (paymentsResponse.items.length > 0) {
        setPayment(paymentsResponse.items[0]);
      }
    } catch (error) {
      console.error('Error loading invoice details:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-10 w-10 text-green-500" />
          </div>

          <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h1>
          <p className="text-gray-600 mb-6">
            {getSuccessMessage()}
          </p>

          {payment && (
            <div className="bg-gray-50 rounded-lg p-6 text-left mb-6">
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Amount Paid</span>
                  <span className="font-semibold text-gray-900">
                    {formatCurrency(payment.amount)}
                  </span>
                </div>
                {invoice && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Invoice</span>
                    <span className="font-medium text-gray-900">
                      #{invoice.invoice_number}
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Date</span>
                  <span className="text-gray-900">
                    {formatDate(payment.processed_at || payment.created_at, 'short')}
                  </span>
                </div>
                {payment.card_last_four && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Payment Method</span>
                    <span className="text-gray-900">
                      {payment.card_brand} •••• {payment.card_last_four}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {payment?.receipt_url && (

            <a href={payment.receipt_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary mb-3"
            >
              <Receipt className="h-4 w-4 mr-2" />
              View Receipt
            </a>
          )}

          <div className="space-y-3">
            {paymentType === 'subscription' ? (
              <button
                onClick={() => navigate('/billing')}
                className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                <Calendar className="h-4 w-4 mr-2" />
                View Subscriptions
              </button>
            ) : invoice ? (
              <button
                onClick={() => navigate(`/billing/invoices/${id}`)}
                className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                <FileText className="h-4 w-4 mr-2" />
                View Invoice
              </button>
            ) : null}

            <button
              onClick={() => navigate('/dashboard')}
              className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              <Home className="h-4 w-4 mr-2" />
              Return to Dashboard
            </button>
          </div>
        </div>

        <p className="text-center text-sm text-gray-500 mt-6">
          A confirmation email has been sent to your registered email address.
        </p>
      </div>
    </div>
  );
}